import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";
import { ApiAnalyticsAggregationDate } from "@/types/analytics/analytics.types.ts";
import {
  ApiEmployeeColumnFilter,
  ApiEmployeeColumnFilters,
} from "@/types/employees/employee-column.types.ts";

export enum ApiPeopleAnalyticsColumnSource {
  employee = "employee",
  custom_field = "cf",
  custom_table = "table",
  time_off = "time_off",
  goals = "goals",
  // Вычисляемая метрика: id `metric.<key>`, агрегации у неё нет
  metric = "metric",
}

export enum ApiPeopleAnalyticsValueType {
  text = "text",
  number = "number",
  money = "money",
  date = "date",
  entity = "entity",
  bool = "bool",
}

export enum ApiPeopleAnalyticsAggregation {
  count = "count",
  count_distinct = "count_distinct",
  sum = "sum",
  avg = "avg",
  min = "min",
  max = "max",
  median = "median",
  values = "values",
}

export enum ApiPeopleAnalyticsWarningCode {
  // Ячейка заполнена: у источника нет истории, показано текущее значение
  no_history = "no_history",
  // Ячейка заполнена: нет права на историю таблицы, показана актуальная строка
  history_access_denied = "history_access_denied",
  // Ячейка пустая: метрике нужно значение на начало периода, а срез не «за период»
  metric_requires_period = "metric_requires_period",
  // У части сумм нет курса к выбранной валюте, в цифру они не вошли
  missing_fx_rate = "missing_fx_rate",
  // У части сумм кастомного поля не указана валюта, в цифру они не вошли
  missing_currency = "missing_currency",
}

export enum ApiPeopleAnalyticsDateGrain {
  day = "day",
  week = "week",
  month = "month",
  quarter = "quarter",
  year = "year",
}

export enum ApiPeopleAnalyticsTimeScopeMode {
  current = "current",
  as_of = "as_of",
  period = "period",
}

export interface ApiPeopleAnalyticsTimeScope {
  mode: ApiPeopleAnalyticsTimeScopeMode;
  date_at?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

export enum ApiPeopleAnalyticsColumnGroup {
  employee_personal = "employee_personal",
  employee_contacts = "employee_contacts",
  employee_position = "employee_position",
  employee_employment = "employee_employment",
  employee_compensation = "employee_compensation",
  employee_additional_compensation = "employee_additional_compensation",
  employee_termination = "employee_termination",
  custom_fields = "custom_fields",
  custom_tables = "custom_tables",
  time_off = "time_off",
  goals = "goals",
  metrics = "metrics",
}

export interface ApiPeopleAnalyticsColumn {
  id: string;
  title: string;
  source_type: ApiPeopleAnalyticsColumnSource;
  value_type: ApiPeopleAnalyticsValueType;
  aggregations: ApiPeopleAnalyticsAggregation[];
  has_history: boolean;
  // Метрики по нескольким записям (число целей, средний прогресс) группой быть не могут
  is_groupable: boolean;
}

export interface ApiPeopleAnalyticsColumnGroupData {
  group: ApiPeopleAnalyticsColumnGroup;
  title: string;
  columns: ApiPeopleAnalyticsColumn[];
}

export interface ApiPeopleAnalyticsCatalog {
  groups: ApiPeopleAnalyticsColumnGroupData[];
}

// Даты приходят строкой, в Date их превращает axios-интерсептор.
export type ApiPeopleAnalyticsCellValue = string | number | boolean | Date | null;

export interface ApiPeopleAnalyticsRow {
  // Строка — группа; без группировки строка одна — итог по всем, group_label = null
  group_label: string | null;
  employee_ids: number[];
  values: ApiPeopleAnalyticsCellValue[];
}

export interface ApiPeopleAnalyticsWarning {
  code: ApiPeopleAnalyticsWarningCode;
  column_id: string;
}

export interface ApiPeopleAnalyticsRows {
  columns: ApiPeopleAnalyticsColumn[];
  rows: ApiPeopleAnalyticsRow[];
  total: number;
  group_by_column: ApiPeopleAnalyticsColumn | null;
  warnings: ApiPeopleAnalyticsWarning[];
  // null — денежных колонок в запросе нет
  currency: string | null;
}

export enum ApiPeopleAnalyticsFormat {
  page = "page",
  xlsx = "xlsx",
}

export interface ApiPeopleAnalyticsEmployeeFilter extends ApiEmployeeColumnFilters {
  text?: string;
  employee_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
  team_ids?: number[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
  employee_lifecycle_statuses?: string[];
  is_manager?: boolean;
}

export interface ApiPeopleAnalyticsColumnInput {
  column_id: string;
  aggregation?: ApiPeopleAnalyticsAggregation;
}

export interface ApiPeopleAnalyticsRowsInput {
  columns: ApiPeopleAnalyticsColumnInput[];
  filter?: ApiPeopleAnalyticsEmployeeFilter;
  group_by_column_id?: string | null;
  group_by_date_grain?: ApiPeopleAnalyticsDateGrain | null;
  limit?: number;
  time_scope?: ApiPeopleAnalyticsTimeScope;
  format?: ApiPeopleAnalyticsFormat;
  compensation?: ApiPeopleAnalyticsCompensationInput;
}

export enum ApiPeopleAnalyticsWidgetType {
  kpi = "kpi",
  line_chart = "line_chart",
  bar_chart = "bar_chart",
  table = "table",
}

export enum ApiPeopleAnalyticsWidgetValueFormat {
  integer = "integer",
  decimal = "decimal",
  percent = "percent",
}

export enum ApiPeopleAnalyticsWidgetScale {
  linear = "linear",
  logarithmic = "logarithmic",
}

export enum ApiPeopleAnalyticsWidgetBucketSort {
  natural = "natural",
  value_asc = "value_asc",
  value_desc = "value_desc",
}

export interface ApiPeopleAnalyticsWidgetMetric {
  key: string;
  column_id: string;
  aggregation?: ApiPeopleAnalyticsAggregation | null;
  // Сотрудник попадает в бакет, если эта дата внутри бакета (найм, увольнение)
  event_date_column_id?: string | null;
  color?: string | null;
}

export interface ApiPeopleAnalyticsWidgetVisualization {
  widget_type: ApiPeopleAnalyticsWidgetType;
  value_format?: ApiPeopleAnalyticsWidgetValueFormat;
  y_scale?: ApiPeopleAnalyticsWidgetScale;
  hide_zero_buckets?: boolean;
  bucket_sort?: ApiPeopleAnalyticsWidgetBucketSort;
  limit?: number | null;
}

export interface ApiPeopleAnalyticsWidgetDashboardRules {
  apply_filter: boolean;
  apply_date_grain: boolean;
  allow_split: boolean;
}

export interface ApiPeopleAnalyticsStoredColumnFilter extends Omit<
  ApiEmployeeColumnFilter,
  "field"
> {
  field?: string;
  table_id?: number | null;
  slug?: string;
  table_type?: string;
  field_id?: number | null;
  field_type?: string | null;
  is_historical?: boolean;
  visible_employee_ids?: number[] | null;
}

export type ApiPeopleAnalyticsWidgetFilter = Omit<
  ApiPeopleAnalyticsEmployeeFilter,
  keyof ApiEmployeeColumnFilters
> &
  Partial<Record<keyof ApiEmployeeColumnFilters, ApiPeopleAnalyticsStoredColumnFilter[]>>;

export interface ApiPeopleAnalyticsWidget {
  id: number;
  title: string;
  system_key: string | null;
  is_system: boolean;
  is_own: boolean;
  metrics: ApiPeopleAnalyticsWidgetMetric[];
  filter: ApiPeopleAnalyticsWidgetFilter;
  group_by_column_id: string | null;
  date_grain: ApiPeopleAnalyticsDateGrain;
  visualization: Required<ApiPeopleAnalyticsWidgetVisualization>;
  dashboard_rules: ApiPeopleAnalyticsWidgetDashboardRules;
  // null — сохранён без валюты, открывается в валюте организации
  compensation: ApiPeopleAnalyticsCompensation | null;
}

export interface ApiPeopleAnalyticsWidgetSaveInput {
  title: string;
  metrics: ApiPeopleAnalyticsWidgetMetric[];
  filter?: ApiPeopleAnalyticsEmployeeFilter;
  group_by_column_id?: string | null;
  date_grain?: ApiPeopleAnalyticsDateGrain;
  visualization: ApiPeopleAnalyticsWidgetVisualization;
  dashboard_rules?: Partial<ApiPeopleAnalyticsWidgetDashboardRules>;
  compensation?: ApiPeopleAnalyticsCompensationInput | null;
}

export interface ApiPeopleAnalyticsDashboardWidget {
  // Колонки сетки из 12
  width: number;
  widget: ApiPeopleAnalyticsWidget;
}

export interface ApiPeopleAnalyticsDashboard {
  id: number;
  title: string;
  system_key: string | null;
  is_system: boolean;
  default_period: string;
  default_date_grain: ApiPeopleAnalyticsDateGrain;
  filter_fields: string[];
  default_compensation: ApiPeopleAnalyticsCompensation | null;
  widgets: ApiPeopleAnalyticsDashboardWidget[];
}

export enum ApiPeopleAnalyticsPayrollAllocation {
  by_days = "by_days",
  period_end = "period_end",
}

export interface ApiPeopleAnalyticsCompensation {
  rollup_type: CompensationRollupType;
  currency: string | null;
  allocation?: ApiPeopleAnalyticsPayrollAllocation;
}

export type ApiPeopleAnalyticsCompensationInput = Partial<ApiPeopleAnalyticsCompensation>;

export interface ApiPeopleAnalyticsWidgetDataInput {
  date_from: Date;
  date_to: Date;
  date_grain?: ApiAnalyticsAggregationDate | null;
  filter?: ApiPeopleAnalyticsEmployeeFilter;
  split_by_column_id?: string | null;
  compensation?: ApiPeopleAnalyticsCompensation | null;
}

export interface ApiPeopleAnalyticsCacheState {
  datetime_built: Date | null;
  employees_count: number;
  values_count: number;
  periods_count: number;
}
